/**
 * Product Customize Drawer
 * Shows main product and accessories when adding to cart
 */

if (!customElements.get('product-customize-drawer')) {
  class ProductCustomizeDrawer extends HTMLElement {
    constructor() {
      super();

      this.mainProductData = null;
      this.accessories = [];
      this.selectedAccessories = new Map();

      this.bindElements();
      this.bindEvents();
    }

    bindElements() {
      this.overlay = this.querySelector('.fixed-overlay');
      this.closeBtn = this.querySelector('.drawer__close-btn');
      this.backBtn = this.querySelector('[data-back-btn]');
      this.mainProductContainer = this.querySelector('[data-main-product]');
      this.accessoriesSection = this.querySelector('[data-accessories-section]');
      this.accessoriesList = this.querySelector('[data-accessories-list]');
      this.itemsCountEl = this.querySelector('[data-items-count]');
      this.itemsToggle = this.querySelector('[data-items-toggle]');
      this.itemsList = this.querySelector('[data-items-list]');
      this.itemsSummary = this.querySelector('[data-items-summary]');
      this.totalPriceEl = this.querySelector('[data-total-price]');
      this.submitBtn = this.querySelector('[data-submit-all]');
      this.submitText = this.querySelector('[data-submit-text]');
      this.mainProductTemplate = document.getElementById('customize-drawer-main-product-template');
      this.accessoryTemplate = document.getElementById('customize-drawer-accessory-template');
    }

    bindEvents() {
      this.overlay?.addEventListener('click', () => this.hide());
      this.closeBtn?.addEventListener('click', () => this.hide());
      this.backBtn?.addEventListener('click', () => this.hide());
      this.submitBtn?.addEventListener('click', () => this.handleSubmitAll());
      this.itemsToggle?.addEventListener('click', () => this.toggleItemsList());

      // Listen for Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.hasAttribute('active')) {
          this.hide();
        }
      });
    }

    toggleItemsList() {
      const isExpanded = this.itemsToggle.getAttribute('aria-expanded') === 'true';
      this.itemsToggle.setAttribute('aria-expanded', !isExpanded);
      
      if (isExpanded) {
        this.itemsList?.setAttribute('hidden', '');
      } else {
        this.itemsList?.removeAttribute('hidden');
      }
    }

    async show(productData, accessories = []) {
      this.mainProductData = productData;
      this.accessories = accessories;
      this.selectedAccessories.clear();

      // Reset items list expanded state
      this.itemsToggle?.setAttribute('aria-expanded', 'false');
      this.itemsList?.setAttribute('hidden', '');

      this.renderMainProduct();
      this.renderAccessories();
      this.updateSummary();

      this.removeAttribute('hidden');
      requestAnimationFrame(() => {
        this.setAttribute('active', '');
        this.setAttribute('open', '');
      });

      document.body.classList.add('overflow-hidden');
      FoxTheme.a11y.trapFocus(this);
    }

    hide() {
      this.removeAttribute('active');
      
      setTimeout(() => {
        this.setAttribute('hidden', '');
        this.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
        FoxTheme.a11y.removeTrapFocus();
      }, 300);
    }

    renderMainProduct() {
      if (!this.mainProductTemplate || !this.mainProductData) return;

      const clone = this.mainProductTemplate.content.cloneNode(true);
      const { product, variant, quantity } = this.mainProductData;

      const imageEl = clone.querySelector('[data-product-image]');
      const titleEl = clone.querySelector('[data-product-title]');
      const variantEl = clone.querySelector('[data-product-variant]');
      const quantityEl = clone.querySelector('[data-product-quantity]');

      if (imageEl) {
        let imageSrc = '';
        if (variant.featured_image?.src) {
          imageSrc = this.getSizedImageUrl(variant.featured_image.src, '160x160');
        } else if (product.featured_image) {
          imageSrc = this.getSizedImageUrl(product.featured_image, '160x160');
        }
        if (imageSrc) {
          imageEl.setAttribute('src', imageSrc);
          imageEl.alt = product.title;
        } else {
          imageEl.classList.add('no-image');
        }
      }

      if (titleEl) titleEl.textContent = product.title;
      
      if (variantEl) {
        if (variant.title && variant.title !== 'Default Title') {
          variantEl.textContent = variant.title;
        } else {
          variantEl.style.display = 'none';
        }
      }

      if (quantityEl) {
        quantityEl.textContent = `${FoxTheme.strings?.quantity || 'Quantity'}: ${quantity}`;
      }

      this.mainProductContainer.innerHTML = '';
      this.mainProductContainer.appendChild(clone);
    }

    renderAccessories() {
      if (!this.accessoryTemplate || !this.accessoriesList) return;

      this.accessoriesList.innerHTML = '';

      if (this.accessories.length === 0) {
        this.accessoriesSection?.setAttribute('hidden', '');
        return;
      }

      this.accessoriesSection?.removeAttribute('hidden');

      this.accessories.forEach((accessory) => {
        const clone = this.accessoryTemplate.content.cloneNode(true);
        const item = clone.querySelector('[data-accessory-item]');
        const variant = accessory.variants[0];

        if (!variant) return;

        item.dataset.variantId = variant.id;
        item.dataset.productHandle = accessory.handle;

        const imageEl = clone.querySelector('[data-accessory-image]');
        const titleEl = clone.querySelector('[data-accessory-title]');
        const quantityInput = clone.querySelector('[data-quantity-input]');
        const minusBtn = clone.querySelector('[data-quantity-minus]');
        const plusBtn = clone.querySelector('[data-quantity-plus]');
        const addBtn = clone.querySelector('[data-add-accessory]');
        const addedBtn = clone.querySelector('[data-added-accessory]');

        if (imageEl) {
          if (accessory.featured_image) {
            imageEl.setAttribute('src', this.getSizedImageUrl(accessory.featured_image, '160x160'));
            imageEl.alt = accessory.title;
          } else {
            imageEl.classList.add('no-image');
          }
        }

        if (titleEl) titleEl.textContent = accessory.title;

        // Bind quantity controls
        minusBtn?.addEventListener('click', () => this.handleQuantityChange(item, -1));
        plusBtn?.addEventListener('click', () => this.handleQuantityChange(item, 1));
        quantityInput?.addEventListener('change', (e) => this.handleQuantityInput(item, e.target));
        
        // Bind add button (no toggle - once added, stays added)
        addBtn?.addEventListener('click', () => this.handleAddAccessory(item));

        this.accessoriesList.appendChild(clone);
      });
    }

    handleQuantityChange(item, delta) {
      const input = item.querySelector('[data-quantity-input]');
      if (!input) return;

      const currentValue = parseInt(input.value) || 0;
      const newValue = Math.max(0, currentValue + delta);
      input.value = newValue;

      // Update state if item is already added
      if (item.classList.contains('is-added')) {
        if (newValue === 0) {
          // If quantity becomes 0 via minus button, remove the item
          this.updateAccessoryState(item, 0, false);
        } else {
          this.updateAccessoryQuantity(item, newValue);
        }
      }
    }

    handleQuantityInput(item, input) {
      const value = Math.max(0, parseInt(input.value) || 0);
      input.value = value;
      
      // Update state if item is already added
      if (item.classList.contains('is-added')) {
        if (value === 0) {
          // If quantity becomes 0, remove the item
          this.updateAccessoryState(item, 0, false);
        } else {
          this.updateAccessoryQuantity(item, value);
        }
      }
    }

    handleAddAccessory(item) {
      const input = item.querySelector('[data-quantity-input]');
      let quantity = parseInt(input?.value) || 0;
      
      // If quantity is 0, set it to 1 when adding
      if (quantity === 0) {
        quantity = 1;
        if (input) input.value = 1;
      }
      
      this.updateAccessoryState(item, quantity, true);
    }

    handleRemoveAccessory(item) {
      // Reset quantity to 0 and remove from selection
      const input = item.querySelector('[data-quantity-input]');
      if (input) input.value = 0;
      this.updateAccessoryState(item, 0, false);
    }

    updateAccessoryQuantity(item, quantity) {
      const variantId = item.dataset.variantId;
      const productHandle = item.dataset.productHandle;
      
      // Find accessory data
      const accessory = this.accessories.find(a => a.handle === productHandle);
      const variant = accessory?.variants?.[0];

      if (this.selectedAccessories.has(variantId)) {
        this.selectedAccessories.set(variantId, {
          quantity: quantity,
          title: accessory?.title || '',
          price: variant?.price || 0
        });
        this.updateSummary();
      }
    }

    updateAccessoryState(item, quantity, forceAdd = false) {
      const variantId = item.dataset.variantId;
      const productHandle = item.dataset.productHandle;
      const addBtn = item.querySelector('[data-add-accessory]');
      const addedBtn = item.querySelector('[data-added-accessory]');

      // Find accessory data
      const accessory = this.accessories.find(a => a.handle === productHandle);
      const variant = accessory?.variants?.[0];

      if (quantity > 0 || forceAdd) {
        this.selectedAccessories.set(variantId, {
          quantity: quantity || 1,
          title: accessory?.title || '',
          price: variant?.price || 0
        });
        item.classList.add('is-added');
        addBtn?.classList.add('hidden');
        addedBtn?.classList.remove('hidden');
      } else {
        this.selectedAccessories.delete(variantId);
        item.classList.remove('is-added');
        addBtn?.classList.remove('hidden');
        addedBtn?.classList.add('hidden');
      }

      this.updateSummary();
    }

    updateSummary() {
      const { product, variant, quantity } = this.mainProductData || {};
      
      // Count total items
      let totalItems = quantity || 1;
      
      // Calculate total price (main product)
      let totalPrice = (variant?.price || 0) * (quantity || 1);
      
      // Build items list for summary
      const itemsList = [];
      
      // Add main product to list
      if (product) {
        itemsList.push({
          quantity: quantity || 1,
          title: product.title
        });
      }

      // Add accessories
      this.selectedAccessories.forEach((data, variantId) => {
        totalItems += data.quantity;
        totalPrice += data.price * data.quantity;
        itemsList.push({
          quantity: data.quantity,
          title: data.title
        });
      });

      // Update items count
      if (this.itemsCountEl) {
        this.itemsCountEl.textContent = totalItems;
      }

      // Update total price
      if (this.totalPriceEl) {
        this.totalPriceEl.textContent = this.formatMoney(totalPrice);
      }

      // Update items summary list
      if (this.itemsSummary) {
        this.itemsSummary.innerHTML = itemsList
          .map(item => `<li>${item.quantity} x ${item.title}</li>`)
          .join('');
      }

      // Update submit button text with item count
      if (this.submitText) {
        this.submitText.textContent = `Add ${totalItems} To Cart`;
      }

      // Enable submit button
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
      }
    }

    // Legacy method for compatibility
    updateItemsCount() {
      this.updateSummary();
    }

    async handleSubmitAll() {
      if (!this.mainProductData) return;

      this.submitBtn?.classList.add('btn--loading');
      this.submitBtn?.setAttribute('disabled', '');

      try {
        // Prepare items to add
        const items = [];

        // Add main product
        items.push({
          id: this.mainProductData.variant.id,
          quantity: this.mainProductData.quantity
        });

        // Add selected accessories
        this.selectedAccessories.forEach((data, variantId) => {
          if (data.quantity > 0) {
            items.push({
              id: parseInt(variantId),
              quantity: data.quantity
            });
          }
        });

        // Add all items to cart
        const response = await this.addItemsToCart(items);

        if (response.ok || response.items) {
          // Dispatch product added event
          document.dispatchEvent(
            new CustomEvent('product-ajax:added', {
              detail: { product: response }
            })
          );

          // Redirect to cart page
          window.location.href = FoxTheme.routes.cart_url || '/cart';
        }
      } catch (error) {
        console.error('Error adding items to cart:', error);
        alert(FoxTheme.strings?.cartError || 'Error adding items to cart. Please try again.');
      } finally {
        this.submitBtn?.classList.remove('btn--loading');
        this.submitBtn?.removeAttribute('disabled');
      }
    }

    async addItemsToCart(items) {
      const response = await fetch(`${FoxTheme.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      return await response.json();
    }

    getSizedImageUrl(src, size) {
      if (!src) return '';
      
      // Handle Shopify CDN URLs
      if (src.includes('cdn.shopify.com')) {
        return src.replace(/(_\d+x\d+)?(\.[^.]+)$/, `_${size}$2`);
      }
      
      return src;
    }

    formatMoney(cents) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }
      
      const value = (parseInt(cents) / 100).toFixed(2);
      return `$${value}`;
    }
  }

  customElements.define('product-customize-drawer', ProductCustomizeDrawer);
}

/**
 * Intercept product form submission to check for accessories
 */
(function() {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomizeDrawerIntercept);
  } else {
    initCustomizeDrawerIntercept();
  }

  function initCustomizeDrawerIntercept() {
    // Listen for product card customize drawer trigger clicks
    document.addEventListener('click', async function(event) {
      const trigger = event.target.closest('.product-card__customize-drawer-trigger');
      if (!trigger) return;

      event.preventDefault();
      
      const productHandle = trigger.dataset.productHandle;
      const variantId = trigger.dataset.variantId;
      const accessoriesData = trigger.dataset.accessories || '';
      
      if (!productHandle) {
        console.error('Product handle not found');
        return;
      }

      // Show loading state
      trigger.classList.add('btn--loading');
      trigger.setAttribute('aria-disabled', 'true');

      try {
        await openCustomizeDrawerForProduct(productHandle, variantId, accessoriesData);
      } finally {
        trigger.classList.remove('btn--loading');
        trigger.removeAttribute('aria-disabled');
      }
    });

    // Listen for form submissions
    document.addEventListener('submit', async function(event) {
      const form = event.target;
      
      // Only intercept product forms (main form, sticky bar, etc.)
      if (!form.matches('form[is="product-form"]') && !form.matches('.main-product-form')) {
        return;
      }

      // Try to find product-info element
      // 1. First check if form is inside product-info
      // 2. If not (e.g., sticky ATC bar), look for product-info on the page
      let productInfo = form.closest('product-info');
      
      if (!productInfo) {
        // Form is not inside product-info (e.g., sticky ATC bar)
        // Look for product-info element on the page
        productInfo = document.querySelector('product-info[data-accessories]');
      }

      if (!productInfo) return;

      // Get accessories from data attribute (set by Liquid)
      const accessoriesData = productInfo.dataset.accessories;
      
      // If no accessories, let form submit normally
      if (!accessoriesData || accessoriesData.trim() === '') {
        return;
      }

      // Parse accessories handles (comma-separated)
      const accessoryHandles = accessoriesData.split(',').map(h => h.trim()).filter(h => h);
      
      if (accessoryHandles.length === 0) {
        return;
      }

      // Has accessories - prevent default and show drawer
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Show loading state on button
      const submitBtn = form.querySelector('[type="submit"]');
      submitBtn?.classList.add('btn--loading');
      submitBtn?.setAttribute('aria-disabled', 'true');

      try {
        await showCustomizeDrawer(form, productInfo, accessoryHandles);
      } finally {
        submitBtn?.classList.remove('btn--loading');
        submitBtn?.removeAttribute('aria-disabled');
      }
    }, true);
  }

  async function showCustomizeDrawer(form, productInfo, accessoryHandles) {
    const drawer = document.querySelector('product-customize-drawer');
    if (!drawer) {
      console.error('Product customize drawer not found');
      return;
    }

    // Get form data
    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity')) || 1;

    // Get product handle
    const productHandle = productInfo.dataset.productHandle || 
                          productInfo.dataset.url?.split('/products/')[1]?.split('?')[0];

    if (!productHandle) {
      console.error('Product handle not found');
      return;
    }

    // Fetch main product data
    let productData;
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      productData = await response.json();
    } catch (e) {
      console.error('Error fetching product data:', e);
      return;
    }

    // Find selected variant
    const selectedVariant = productData.variants.find(v => v.id == variantId) || productData.variants[0];

    // Fetch all accessory products in parallel
    const accessoriesProducts = await Promise.all(
      accessoryHandles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) throw new Error(`Failed to fetch ${handle}`);
          return await response.json();
        } catch (e) {
          console.error(`Error fetching accessory ${handle}:`, e);
          return null;
        }
      })
    );

    // Filter out failed fetches
    const validAccessories = accessoriesProducts.filter(p => p !== null);

    if (validAccessories.length === 0) {
      console.warn('No valid accessories found, submitting form normally');
      // Submit form normally if no accessories found
      form.requestSubmit();
      return;
    }

    // Show drawer with products
    drawer.show(
      {
        product: productData,
        variant: selectedVariant,
        quantity: quantity
      },
      validAccessories
    );
  }

  /**
   * Handle add to cart for a product (triggered from product cards)
   * Opens drawer if accessories exist, otherwise adds directly to cart
   */
  async function openCustomizeDrawerForProduct(productHandle, variantId, accessoriesData) {
    // Parse accessories from data attribute (comma-separated handles)
    let accessoryHandles = [];
    if (accessoriesData && accessoriesData.trim()) {
      accessoryHandles = accessoriesData.split(',').map(h => h.trim()).filter(h => h);
    }

    // If NO accessories, add directly to cart
    if (accessoryHandles.length === 0) {
      await addProductDirectlyToCart(variantId);
      return;
    }

    // Has accessories - open the drawer
    const drawer = document.querySelector('product-customize-drawer');
    if (!drawer) {
      // Fallback: add directly to cart if drawer not found
      console.warn('Product customize drawer not found, adding directly to cart');
      await addProductDirectlyToCart(variantId);
      return;
    }

    // Fetch product data
    let productData;
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      productData = await response.json();
    } catch (e) {
      console.error('Error fetching product data:', e);
      return;
    }

    // Find selected variant
    const selectedVariant = variantId 
      ? productData.variants.find(v => v.id == variantId) || productData.variants[0]
      : productData.variants[0];

    // Fetch accessory products
    const accessoriesProducts = await Promise.all(
      accessoryHandles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) throw new Error(`Failed to fetch ${handle}`);
          return await response.json();
        } catch (e) {
          console.error(`Error fetching accessory ${handle}:`, e);
          return null;
        }
      })
    );

    // Filter out failed fetches
    const validAccessories = accessoriesProducts.filter(p => p !== null);

    // If all accessories failed to load, add directly to cart
    if (validAccessories.length === 0) {
      console.warn('No valid accessories found, adding directly to cart');
      await addProductDirectlyToCart(variantId);
      return;
    }

    // Show drawer with product and accessories
    drawer.show(
      {
        product: productData,
        variant: selectedVariant,
        quantity: 1
      },
      validAccessories
    );
  }

  /**
   * Add a product directly to cart (no drawer)
   */
  async function addProductDirectlyToCart(variantId) {
    try {
      const response = await fetch(`${FoxTheme.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1
          }]
        })
      });

      const result = await response.json();

      if (response.ok || result.items) {
        // Dispatch product added event
        document.dispatchEvent(
          new CustomEvent('product-ajax:added', {
            detail: { product: result }
          })
        );

        // Redirect to cart page (consistent with drawer behavior)
        window.location.href = FoxTheme.routes.cart_url || '/cart';
      } else {
        // Handle error
        console.error('Error adding to cart:', result);
        alert(result.description || FoxTheme.strings?.cartError || 'Error adding item to cart. Please try again.');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert(FoxTheme.strings?.cartError || 'Error adding item to cart. Please try again.');
    }
  }
})();

